import {Empty} from 'antd';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';

import {Spinner} from '../../../../layout/spinner/spinner';
import {ResponseTemplateDataType} from '../../../../service/reviews/reviews-response-templates';
import {classNames} from '../../../../util/css';

import * as styles from './response-template-select.scss';

type PropsType = {
    templates: Array<ResponseTemplateDataType>;
    isLoading: boolean;
    isOpen: boolean;
    onClickOutside: () => void;
    onTemplateSelect: (template: ResponseTemplateDataType) => void;
};

export function ResponseTemplateSelect(props: PropsType): JSX.Element {
    const {templates, isLoading, isOpen, onTemplateSelect, onClickOutside} = props;
    const quickTemplateMenuRef = useRef<HTMLDivElement>(null);
    const itemReferences = useRef<Array<HTMLButtonElement | null>>([]);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    // Сброс выбранного индекса при открытии меню
    useEffect(() => {
        if (isOpen) {
            setSelectedIndex(0);
        }
    }, [isOpen]);

    useLayoutEffect(() => {
        if (isOpen && itemReferences.current[selectedIndex] && quickTemplateMenuRef.current) {
            const selectedElement = itemReferences.current[selectedIndex];

            if (selectedElement) {
                selectedElement.focus();
            }
        }
    }, [selectedIndex, isOpen]);

    // Обработка клавиатуры
    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent): void {
            if (!isOpen || !templates || templates.length === 0) {
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedIndex((previous) => (previous + 1) % templates.length);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedIndex((previous) => (previous - 1 + templates.length) % templates.length);
            } else if (event.key === 'Enter' && selectedIndex >= 0 && selectedIndex < templates.length) {
                event.preventDefault();
                const selectedTemplate = templates[selectedIndex];

                if (selectedTemplate) {
                    onTemplateSelect(selectedTemplate);
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, templates, selectedIndex, onTemplateSelect]);

    // Обработка клика вне Popover
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (!isOpen) {
                return;
            }

            if (!(event.target instanceof Node)) {
                return;
            }

            const target = event.target;

            // Проверяем, что клик был вне Popover контента и стрелки
            const isClickInPopover = quickTemplateMenuRef.current?.contains(target);

            // Если клик был вне TextArea и вне Popover - закрываем меню
            if (!isClickInPopover) {
                onClickOutside();
            }
        }

        // Используем mousedown вместо click для более раннего срабатывания
        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [isOpen, onClickOutside]);

    if (isLoading) {
        return (
            <div className={styles.ResponseTemplateSelect_loadingWrapper}>
                <Spinner />
            </div>
        );
    }

    if (!templates || templates.length === 0) {
        return <Empty />;
    }

    return (
        <div className={styles.ResponseTemplateSelect_container} ref={quickTemplateMenuRef}>
            {templates.map((template, index) => (
                <button
                    className={classNames(styles.ResponseTemplateSelect_item, {
                        [styles.ResponseTemplateSelect_item__selected]: selectedIndex === index,
                    })}
                    key={template.id}
                    onClick={() => onTemplateSelect(template)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    ref={(element) => {
                        itemReferences.current[index] = element;
                    }}
                    type="button"
                >
                    <div className={styles.ResponseTemplateSelect_itemTitle}>{template.title}</div>
                    <div className={styles.ResponseTemplateSelect_itemMessage}>{template.message}</div>
                </button>
            ))}
        </div>
    );
}
